'use strict';

import { updateUIFromShadowRoot } from '@core/i18n/window-language';
import { UPDATE_LANGUAGE_EVENT } from '@core/constants/EVENTS';
import {
    globalSharedStyles,
    customCommonStyles,
    typographySharedStyles,
} from '@core/styles';
import htmlContent from './UpdateReportsComponent.html';
import cssContent from './UpdateReportsComponent.css';
import { handleUpdateReportsClick } from './utils';

/**
 * UpdateReportsComponent - Web component for handling report updates
 *
 * This component provides functionality to update reports and handle
 * language changes with proper event management and cleanup.
 *
 * @class UpdateReportsComponent
 * @extends {HTMLElement}
 */
class UpdateReportsComponent extends HTMLElement {
    constructor() {
        super();
        this.attachShadow({ mode: 'open' });

        // Initialize properties
        this._updateReportsButton = null;
        this._handleTranslationHelperBound = null;

        // Create template and styles
        const template = document.createElement('template');
        template.innerHTML = htmlContent;

        const styleSheet = new CSSStyleSheet();
        styleSheet.replaceSync(cssContent);

        this.shadowRoot.adoptedStyleSheets = [
            globalSharedStyles,
            customCommonStyles,
            typographySharedStyles,
            styleSheet,
        ];

        this.shadowRoot.appendChild(template.content.cloneNode(true));

        // Bind event handlers
        this._handleTranslationHelperBound = this._handleTranslationHelper.bind(this);
        this._handleUpdateReportsClickBound = this._handleUpdateReportsClick.bind(this);
    }

    /**
     * Called when the element is added to the DOM
     * Sets up event listeners and caches DOM references
     */
    connectedCallback() {
        this._cacheDOMElements();
        this._addEventListeners();
    }

    /**
     * Called when the element is removed from the DOM
     * Cleans up event listeners to prevent memory leaks
     */
    disconnectedCallback() {
        this._removeEventListeners();
    }

    /**
     * Cache DOM element references for better performance
     * @private
     */
    _cacheDOMElements() {
        this._updateReportsButton = this.shadowRoot.querySelector(
            '[data-update-reports-button]',
        );
    }

    /**
     * Add event listeners to the component
     * @private
     */
    _addEventListeners() {
        if (!this._updateReportsButton) {
            console.warn('UpdateReportsComponent: Update reports button not found');
            return;
        }

        // Add language change listener
        document.addEventListener(
            UPDATE_LANGUAGE_EVENT,
            this._handleTranslationHelperBound,
            { passive: true },
        );

        // Add click listener to update reports button
        this._updateReportsButton.addEventListener(
            'click',
            this._handleUpdateReportsClickBound,
        );
    }

    /**
     * Remove event listeners to prevent memory leaks
     * @private
     */
    _removeEventListeners() {
        document.removeEventListener(
            UPDATE_LANGUAGE_EVENT,
            this._handleTranslationHelperBound,
        );

        if (this._updateReportsButton) {
            this._updateReportsButton.removeEventListener(
                'click',
                this._handleUpdateReportsClickBound,
            );
        }
    }

    /**
     * Handle translation updates when language changes
     * @private
     */
    _handleTranslationHelper() {
        updateUIFromShadowRoot(this.shadowRoot);
    }

    /**
     * Handle update reports button click
     * @private
     */
    _handleUpdateReportsClick() {
        handleUpdateReportsClick();
    }
}

customElements.define('update-reports-component', UpdateReportsComponent);
